import { Update } from '~/mvuReducer'
import * as PubSub from '../../pubSub'
import { Toast } from '~/toasts/toasts'
import { getLocalStorage, setLocalStorage } from '~/localStorage'
import { Init } from '~/mvuReducer'

export type State = {
  selectedDeviceId?: string
  availableInputs: MediaDeviceInfo[]
}

type ChangeInput = { action: 'change-input'; deviceId: string }
type MediaDevicesEnumerated = { action: 'media-devices-enumerated'; audioInputDevices: MediaDeviceInfo[] }
type EnumerateDevices = { action: 'enumerate-media-devices' }

export type Action = ChangeInput | MediaDevicesEnumerated | EnumerateDevices

export type Dispatch = (action: Action) => void

// Chrome (and maybe other browsers, but not Safari) treat the string `default` as special,
// and starting a recording with `deviceId: 'default'` is more reliable than `deviceId: undefined`.
export const defaultMicrophoneDeviceId = 'default'

type SaveDevicePreferenceEffect = { effect: 'save-device-preference'; deviceId: string; devices: MediaDeviceInfo[] }
type EnumerateMediaDevicesEffect = { effect: 'enumerate-media-devices' }
type InitializeDeviceWatcher = { effect: 'initialize-device-watcher' }

export type SideEffect = SaveDevicePreferenceEffect | EnumerateMediaDevicesEffect | InitializeDeviceWatcher

export function initialize(): Init<State, SideEffect, Action> {
  return {
    state: { availableInputs: [] },
    sideEffects: [{ effect: 'enumerate-media-devices' }, { effect: 'initialize-device-watcher' }],
    subscriptions: [],
  }
}

export function applyAction(state: State, action: Action): Update<State, SideEffect> {
  switch (action.action) {
    case 'change-input':
      return [
        { ...state, selectedDeviceId: action.deviceId },
        [{ effect: 'save-device-preference', deviceId: action.deviceId, devices: state.availableInputs }],
      ]

    case 'media-devices-enumerated':
      const availableInputs = action.audioInputDevices.map(handlePrivacyPreventedDeviceDetection)
      const deviceFromPrefs = getDevicePreference()

      // Try current selection first, then stored preference, then first available
      const selectedDeviceId = hasDevice(availableInputs, state.selectedDeviceId)
        ? state.selectedDeviceId
        : hasDevice(availableInputs, deviceFromPrefs)
          ? deviceFromPrefs
          : availableInputs[0]?.deviceId

      return [{ ...state, availableInputs, selectedDeviceId }, []]

    case 'enumerate-media-devices':
      return [state, [{ effect: 'enumerate-media-devices' }]]
  }
}

export async function executeSideEffect(sideEffect: SideEffect, dispatch: Dispatch): Promise<void> {
  switch (sideEffect.effect) {
    case 'enumerate-media-devices': {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        const audioInputDevices = devices.filter((d) => d.kind === 'audioinput')
        dispatch({ action: 'media-devices-enumerated', audioInputDevices })
      } catch (error) {
        debugError('Error enumerating media devices:', error)

        const toast: Toast = { type: 'error', content: 'Failed to detect audio devices', durationMs: 5000 }
        PubSub.publish('show-toast', toast)
      }
      break
    }

    case 'save-device-preference':
      const name = getDeviceName(sideEffect.devices, sideEffect.deviceId)
      debugLog('Saving input preference for microphone', name)

      setDevicePreference(sideEffect.deviceId)
      break

    case 'initialize-device-watcher':
      navigator.mediaDevices.addEventListener('devicechange', () => {
        dispatch({ action: 'enumerate-media-devices' })
      })
      break
  }
}

export function getDeviceName(devices: MediaDeviceInfo[], deviceId: string | undefined): string {
  if (!deviceId) {
    return 'Default microphone'
  }

  const device = devices.find((d) => d.deviceId === deviceId)
  return device?.label ? device.label : deviceId
}

export function debugDeviceName(devices: MediaDeviceInfo[], deviceId: string | undefined): string {
  return `${getDeviceName(devices, deviceId)} (ID ${deviceId})`
}

function hasDevice(devices: MediaDeviceInfo[], deviceId: string | undefined): boolean {
  return !!deviceId && devices.some((d) => d.deviceId === deviceId)
}

function handlePrivacyPreventedDeviceDetection(device: MediaDeviceInfo): MediaDeviceInfo {
  return device.deviceId ? device : { ...device, deviceId: defaultMicrophoneDeviceId, label: 'Default microphone' }
}

// Note that unlike most recording-related data, the preferred device ID is
// *global* to all recordings.
function getDevicePreference(): string | undefined {
  return getLocalStorage<string | undefined>('jump', 'deviceId', undefined)
}

function setDevicePreference(deviceId: string): void {
  setLocalStorage('jump', 'deviceId', deviceId)
}

function debugLog(...args: any[]): void {
  /* eslint-disable-next-line no-console */
  console.log('[Microphone input]', ...args)
}

function debugError(...args: any[]): void {
  /* eslint-disable-next-line no-console */
  console.error('[Microphone input]', ...args)
}
